import React, { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useTranslation } from "react-i18next";
import { primaryColor, tooltipContentStyle } from "utils/dashboardUtils";
import { formatLabel, isDesktop } from "utils/utils";

const VOLUME_REVENUE_DATA = {
  ALL: [
    { time: "9AM", volume: 0, revenue: 0 },
    { time: "10AM", volume: 0, revenue: 0 },
    { time: "11AM", volume: 0, revenue: 0 },
    { time: "12PM", volume: 0, revenue: 0 },
    { time: "1PM", volume: 0, revenue: 0 },
    { time: "2PM", volume: 0, revenue: 0 },
    { time: "3PM", volume: 0, revenue: 0 },
    { time: "4PM", volume: 0, revenue: 0 },
    { time: "5PM", volume: 0, revenue: 0 },
    { time: "6PM", volume: 0, revenue: 0 },
    { time: "7PM", volume: 0, revenue: 0 },
  ],
  INTERIOR: [
    { time: "9AM", volume: 0, revenue: 0 },
    { time: "10AM", volume: 0, revenue: 0 },
    { time: "11AM", volume: 0, revenue: 0 },
    { time: "12PM", volume: 0, revenue: 0 },
    { time: "1PM", volume: 0, revenue: 0 },
    { time: "2PM", volume: 0, revenue: 0 },
    { time: "3PM", volume: 0, revenue: 0 },
    { time: "4PM", volume: 0, revenue: 0 },
    { time: "5PM", volume: 0, revenue: 0 },
    { time: "6PM", volume: 0, revenue: 0 },
    { time: "7PM", volume: 0, revenue: 0 },
  ],
  EXTERIOR: [
    { time: "9AM", volume: 0, revenue: 0 },
    { time: "10AM", volume: 0, revenue: 0 },
    { time: "11AM", volume: 0, revenue: 0 },
    { time: "12PM", volume: 0, revenue: 0 },
    { time: "1PM", volume: 0, revenue: 0 },
    { time: "2PM", volume: 0, revenue: 0 },
    { time: "3PM", volume: 0, revenue: 0 },
    { time: "4PM", volume: 0, revenue: 0 },
    { time: "5PM", volume: 0, revenue: 0 },
    { time: "6PM", volume: 0, revenue: 0 },
    { time: "7PM", volume: 0, revenue: 0 },
  ],
};

const VolumeRevenueTrend = ({ revenueData, activeFilterType }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("ALL");

  const allData = useMemo(() => {
    if (!Array.isArray(revenueData)) return [];
    return revenueData.map((item) => ({
      time: item.label,
      volume: item.totalVolume ?? 0,
      revenue: item.totalRevenue ?? 0,
    }));
  }, [revenueData]);

  const data = VOLUME_REVENUE_DATA[mode];
  // const data = mode === "ALL" ? allData : VOLUME_REVENUE_DATA[mode];

  return (
    <div className="dashboard_common_card">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h3 className="fs_20">{t("Volume And Revenue Trend")}</h3>
          <p className="page_sub_title mt-2">
            {t(
              "Hourly vehicle processing rate — helps optimize staffing and workflow.",
            )}
          </p>
        </div>

        <div className="heatmap_filters">
          {["ALL", "INTERIOR", "EXTERIOR"]?.map((item) => (
            <button
              key={item}
              className={mode === item ? "active" : ""}
              onClick={() => setMode(item)}
            >
              {item.charAt(0) + item.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={data}
          margin={{ top: 10, right: 40, left: 30, bottom: 0 }}
        >
          <defs>
            <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={primaryColor} stopOpacity={0.4} />
              <stop offset="100%" stopColor={primaryColor} stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid
            vertical={false}
            stroke="#a0acb926"
            strokeDasharray="0"
          />

          <XAxis
            dataKey="time"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#8B95A7", fontSize: 12 }}
            padding={{ left: 0, right: 0 }}
            tickFormatter={(label) => formatLabel(label, activeFilterType)}
            interval={isDesktop ? 0 : 1}
          />

          <YAxis hide />

          <Tooltip
            formatter={(value, name) => [
              value,
              name === "volume" ? t("Volume") : t("Revenue"),
            ]}
            labelFormatter={(label) => formatLabel(label, activeFilterType)} // ✅ added
            contentStyle={tooltipContentStyle}
          />

          <Area
            type="monotone"
            dataKey="volume"
            stroke={primaryColor}
            strokeWidth={2}
            fill="url(#volumeGradient)"
            dot={{ r: 4, fill: "#fff", stroke: primaryColor, strokeWidth: 2 }}
            activeDot={{ r: 6 }}
          />

          <Line
            type="monotone"
            dataKey="revenue"
            stroke="#9AA6B2"
            strokeWidth={2}
            strokeDasharray="3 4"
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default VolumeRevenueTrend;
